/**
 * Keycloak Authentication Provider
 * 
 * This module initializes and manages Keycloak authentication for the portal.
 * It provides a singleton Keycloak instance and React hooks for auth state.
 * 
 * Configuration is loaded from /config.json at runtime, allowing the same
 * Docker image to be used across environments (staging, production).
 */

import Keycloak from 'keycloak-js';
import { getConfigSync, type PortalConfig } from './config';

/**
 * Singleton Keycloak instance
 */
let keycloakInstance: Keycloak | null = null;

/**
 * Track initialization state to prevent double-init in React Strict Mode
 */
let initializationPromise: Promise<boolean> | null = null;
let isInitialized = false;

/**
 * Session state tracking
 * - sessionExpired: true when refresh token is invalid (user must re-login)
 * - onSessionExpiredCallback: called when session expires
 * - onTokenRefreshedCallback: called when token is successfully refreshed
 */
let sessionExpired = false;
let onSessionExpiredCallback: (() => void) | null = null;
let onTokenRefreshedCallback: ((token: string) => void) | null = null;

/**
 * Check if the session has expired (refresh token invalid)
 */
export function isSessionExpired(): boolean {
  return sessionExpired;
}

/**
 * Register callbacks for session events
 */
export function setSessionCallbacks(
  onExpired: () => void,
  onRefreshed?: (token: string) => void
): void {
  onSessionExpiredCallback = onExpired;
  onTokenRefreshedCallback = onRefreshed || null;
}

/**
 * Check if an error indicates the refresh token is invalid
 */
function isInvalidGrantError(error: unknown): boolean {
  // keycloak-js doesn't expose structured errors well, but we can check the message
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return msg.includes('invalid_grant') || msg.includes('refresh token');
  }
  return false;
}

/**
 * Get or create the Keycloak instance.
 * 
 * IMPORTANT: getConfig() must be awaited before calling this function.
 * The config is loaded at app startup in main.tsx.
 */
export function getKeycloak(): Keycloak {
  if (!keycloakInstance) {
    const config = getConfigSync();
    keycloakInstance = new Keycloak({
      url: config.keycloakUrl,
      realm: config.keycloakRealm,
      clientId: config.keycloakClientId,
    });
  }
  return keycloakInstance;
}

/**
 * Initialize Keycloak and check for existing session
 * 
 * Handles React Strict Mode double-mounting by returning cached promise.
 * 
 * @param onAuthenticated - Callback when user is authenticated
 * @param onUnauthenticated - Callback when user is not authenticated
 */
export async function initKeycloak(
  onAuthenticated?: () => void,
  onUnauthenticated?: () => void
): Promise<boolean> {
  const keycloak = getKeycloak();
  
  // If already initialized, just check current state and call callbacks
  if (isInitialized) {
    // console.log('[Keycloak] Already initialized, returning cached state');
    if (keycloak.authenticated) {
      onAuthenticated?.();
    } else {
      onUnauthenticated?.();
    }
    return keycloak.authenticated ?? false;
  }
  
  // If initialization is in progress (React Strict Mode double-mount), return the existing promise
  if (initializationPromise) {
    // console.log('[Keycloak] Initialization already in progress, waiting...');
    const result = await initializationPromise;
    if (keycloak.authenticated) {
      onAuthenticated?.();
    } else {
      onUnauthenticated?.();
    }
    return result;
  }
  
  // Start initialization
  initializationPromise = (async () => {
    try {
      // Detect if we're running through the Caddy proxy (port 4200) or directly (port 3000)
      // Silent SSO only works through Caddy because it proxies /auth to Keycloak
      const isThroughProxy = window.location.port === '4200' || window.location.port === '';
      
      // check-sso will silently check if user is already logged in
      // login-required would force a redirect to login page
      const authenticated = await keycloak.init({
        // Only use check-sso if we're going through the proxy, otherwise just check existing token
        onLoad: isThroughProxy ? 'check-sso' : undefined,
        silentCheckSsoRedirectUri: isThroughProxy ? window.location.origin + '/silent-check-sso.html' : undefined,
        pkceMethod: 'S256',
        checkLoginIframe: isThroughProxy, // Enable iframe check when on same domain (proxied)
        checkLoginIframeInterval: 5, // Check every 5 seconds
        enableLogging: true, // Help debug issues
      });

      isInitialized = true;
      
      if (authenticated) {
        // console.log('[Keycloak] User is authenticated');
        onAuthenticated?.();
        
        // Setup token refresh
        setupTokenRefresh(keycloak);
      } else {
        // console.log('[Keycloak] User is not authenticated');
        onUnauthenticated?.();
      }

      return authenticated;
    } catch (error) {
      console.error('[Keycloak] Initialization failed:', error);
      isInitialized = true; // Mark as initialized even on failure to prevent retry loops
      onUnauthenticated?.();
      return false;
    }
  })();
  
  return initializationPromise;
}

/**
 * Setup automatic token refresh before it expires
 * 
 * Behavior:
 * - If session already expired, skip refresh attempts
 * - On successful refresh, notify callback with new token
 * - On network/temporary errors, log and retry next interval
 * - On invalid_grant (refresh token dead), mark session expired and notify
 */
function setupTokenRefresh(keycloak: Keycloak): void {
  // Refresh token when it's about to expire (30 seconds before)
  setInterval(async () => {
    // Don't try to refresh if session is already expired
    if (sessionExpired) {
      return;
    }
    
    if (keycloak.authenticated) {
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          // console.log('[Keycloak] Token refreshed');
          // Notify callback with new token
          if (onTokenRefreshedCallback && keycloak.token) {
            onTokenRefreshedCallback(keycloak.token);
          }
        }
      } catch (error) {
        console.error('[Keycloak] Token refresh failed:', error);
        
        // Check if this is a permanent failure (refresh token invalid)
        if (isInvalidGrantError(error)) {
          console.error('[Keycloak] Refresh token invalid - session expired');
          sessionExpired = true;
          onSessionExpiredCallback?.();
        }
        // For other errors (network, etc.), just log and let the interval retry
        // The access token may still be valid for a bit
      }
    }
  }, 60000); // Check every 60 seconds (was 10 - too aggressive)
  
  // Also set up the onTokenExpired callback as a backup
  keycloak.onTokenExpired = async () => {
    // console.log('[Keycloak] Access token expired, attempting refresh...');
    
    if (sessionExpired) {
      return;
    }
    
    try {
      await keycloak.updateToken(-1); // Force refresh (token already expired)
      // console.log('[Keycloak] Token refreshed after expiration');
      if (onTokenRefreshedCallback && keycloak.token) {
        onTokenRefreshedCallback(keycloak.token);
      }
    } catch (error) {
      console.error('[Keycloak] Token refresh after expiration failed:', error);
      
      if (isInvalidGrantError(error)) {
        console.error('[Keycloak] Refresh token invalid - session expired');
        sessionExpired = true;
        onSessionExpiredCallback?.();
      }
      // Otherwise, the interval will keep trying
    }
  };
}

/**
 * Login - redirect to Keycloak login page
 */
export function login(redirectUri?: string): void {
  const keycloak = getKeycloak();
  keycloak.login({
    redirectUri: redirectUri || window.location.href,
  });
}

/**
 * Logout - redirect to Keycloak logout page
 */
export function logout(redirectUri?: string): void {
  const keycloak = getKeycloak();
  // Build full absolute URL for post_logout_redirect_uri
  // Keycloak requires this to be an exact match in the client's valid redirect URIs
  const fullRedirectUri = redirectUri 
    ? (redirectUri.startsWith('http') ? redirectUri : `${window.location.origin}${redirectUri}`)
    : window.location.origin;
  
  keycloak.logout({
    redirectUri: fullRedirectUri,
  });
}

/**
 * Get the current access token
 */
export function getToken(): string | undefined {
  return getKeycloak().token;
}

/**
 * Get the current user profile from the token
 */
export function getUserProfile(): KeycloakUserProfile | null {
  const keycloak = getKeycloak();
  
  if (!keycloak.authenticated || !keycloak.tokenParsed) {
    return null;
  }

  const token = keycloak.tokenParsed as KeycloakTokenParsed;
  
  return {
    id: token.sub || '',
    email: token.email || '',
    name: token.name || token.preferred_username || '',
    firstName: token.given_name,
    lastName: token.family_name,
    picture: token.picture,
  };
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getKeycloak().authenticated ?? false;
}

// Types for Keycloak token claims
interface KeycloakTokenParsed {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
}

export interface KeycloakUserProfile {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  picture?: string;
}
